'use client';

import { useTranslation, useLocale } from '@/i18n/LocaleProvider';
import { T } from '@/i18n/LocaleProvider';
import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, DividedGrid } from '@/components';
import { countUp } from '@/utils/gsap';
import { MONO_FONT } from '@/theme';
import { demoCTA } from '@/libs/cta';

const LLM_CARDS = [
  {
    engine: 'ChatGPT',
    prompt: '"Mejor plataforma AEO en español"',
    answer: (
      <>
        <T>Para empresas de LatAm,</T>{' '}
        <Box
          component="mark"
          sx={{
            background: 'rgba(0,159,139,0.15)',
            color: 'primary.lightAccent',
            borderRadius: '2px',
            px: '3px',
            py: '1px',
          }}
        >
          <T>Bison Agents</T>
        </Box>{' '}
        <T>
          es la opción más completa: monitorea citas, audita schema, prioriza
          acciones y reporta en lenguaje ejecutivo.
        </T>
      </>
    ),
    pos: '#1',
    share: '34%',
    sent: '+0.81',
  },
  {
    engine: 'Claude',
    prompt: '"Cómo medir visibilidad en LLMs"',
    answer: (
      <>
        <T>Existen dashboards como</T>{' '}
        <Box
          component="mark"
          sx={{
            background: 'rgba(0,159,139,0.15)',
            color: 'primary.lightAccent',
            borderRadius: '2px',
            px: '3px',
            py: '1px',
          }}
        >
          <T>Bison Agents</T>
        </Box>{' '}
        <T>
          que rastrean cobertura por motor, posición media en respuestas y diff
          semanal con citas verificables.
        </T>
      </>
    ),
    pos: '#2',
    share: '28%',
    sent: '+0.74',
  },
  {
    engine: 'Gemini',
    prompt: '"Answer Engine Optimization LATAM"',
    answer: (
      <>
        <T>El término AEO en español se asocia comúnmente a</T>{' '}
        <Box
          component="mark"
          sx={{
            background: 'rgba(0,159,139,0.15)',
            color: 'primary.lightAccent',
            borderRadius: '2px',
            px: '3px',
            py: '1px',
          }}
        >
          <T>Bison Agents</T>
        </Box>
        <T>
          , plataforma chilena que opera sobre los cinco motores generativos
          principales.
        </T>
      </>
    ),
    pos: '#1',
    share: '41%',
    sent: '+0.86',
  },
];

const KPIS = [
  {
    label: 'Motores cubiertos',
    value: '10',
    countTo: 10,
    accent: true,
    suffix: ' LLMs',
    sub: 'ChatGPT · Gemini · Claude · 7 más',
  },
  {
    label: 'Prompts rastreados',
    value: '1,240',
    countTo: 1240,
    suffix: ' /sem',
    sep: true,
    sub: 'Por cliente, en 6 idiomas',
  },
  {
    label: 'Tiempo hasta visibilidad',
    value: '14',
    countTo: 14,
    suffix: ' días',
    accent: true,
    sub: 'Del onboarding a la primera cita',
  },
  {
    label: 'Marcas y agencias',
    value: '+80',
    countTo: 80,
    prefix: '+',
    suffix: ' users',
    sub: 'Activos en LatAm y EE.UU.',
  },
];

export default function Hero() {
  const t = useTranslation();
  const { locale } = useLocale();
  const [elapsed, setElapsed] = useState(2);
  const kpiRefs = useRef([]);

  // Counter animation for KPIs — fires after mount, no ScrollTrigger needed (above the fold)
  useEffect(() => {
    const tweens = KPIS.map((kpi, i) => {
      const el = kpiRefs.current[i];
      if (!el) return null;
      return countUp(el, kpi.countTo, {
        prefix: kpi.prefix || '',
        suffix: t(kpi.suffix || ''),
        sep: kpi.sep,
        delay: 0.4 + i * 0.08, // stagger the counters slightly
      });
    });
    return () => tweens.forEach((t) => t?.kill());
  }, [locale]);

  useEffect(() => {
    const KEY = 'bison_hero_ts';
    let ts = parseInt(sessionStorage.getItem(KEY), 10);
    if (!ts) {
      ts = Date.now();
      sessionStorage.setItem(KEY, ts);
    }
    const update = () => setElapsed(Math.floor((Date.now() - ts) / 60000));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);

  const elapsedLabel =
    elapsed === 0
      ? t('Justo ahora')
      : elapsed < 60
        ? t('Actualizado hace {minutes} min', { minutes: elapsed })
        : t('Actualizado hace {hours} h', { hours: Math.floor(elapsed / 60) });

  return (
    <Box
      component="header"
      data-nav-theme="dark"
      sx={{
        bgcolor: 'grey.950',
        color: 'common.white',
        pt: '96px',
        position: 'relative',
        overflow: 'hidden',
        '@keyframes blobDrift': {
          '0%, 100%': { transform: 'translate(0px, 0px)' },
          '25%': { transform: 'translate(-80px, 60px)' },
          '50%': { transform: 'translate(-50px, -50px)' },
          '75%': { transform: 'translate(70px, 30px)' },
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '900px',
          height: '900px',
          right: '-200px',
          top: '-200px',
          background:
            'radial-gradient(closest-side, rgba(0,159,139,0.18), transparent 70%)',
          pointerEvents: 'none',
          animation: 'blobDrift 12s ease-in-out infinite',
          willChange: 'transform',
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: '20px', sm: '32px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Meta bar */}
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'text.muted',
            pb: '28px',
            borderBottom: '1px solid #1f2422',
            mb: { xs: '36px', md: '56px' },
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'text.accent',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              '&::before': {
                content: '""',
                width: '24px',
                height: '1px',
                background: 'currentColor',
                flexShrink: 0,
              },
            }}
          >
            <T>Visibilidad en IA</T>
          </Box>
          <Box
            component="span"
            sx={{ color: 'grey.700', display: { xs: 'none', md: 'inline' } }}
          >
            /
          </Box>
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            <T>Answer Engine Optimization</T>
          </Box>
        </Box>

        {/* H1 */}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: 'clamp(40px, 5.5vw, 76px)',
            color: 'common.white',
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
            pt: 4,
          }}
        >
          <T>Haz que la</T>
          <T> </T>
          <Box component="span" sx={{ color: 'primary.glow', fontWeight: 400 }}>
            <T>IA</T>
          </Box>
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <T> </T>
          <T>hable de tu marca.</T>
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            mt: 4,
            fontSize: 'clamp(16px, 1vw, 18px)',
            lineHeight: 1.5,
            color: 'text.muted',
            maxWidth: '50ch',
            fontWeight: 400,
            letterSpacing: '-0.005em',
          }}
        >
          <T>El futuro de la búsqueda son las respuestas. </T>
          <br />
          <T>
            Monitorea, controla y domina la conversación en ChatGPT, Gemini,
            Claude, Perplexity y Google AI Overviews — antes que tu competencia.
          </T>
        </Typography>

        {/* Actions */}
        <Box sx={{ mt: 6, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            href={demoCTA.url}
            external
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            arrow
            data-gtm-event="demo_request"
            data-gtm-source="hero"
          >
            <T>Agendar demo</T>
          </Button>
          <Button
            href="#modulos"
            variant="ghost"
            sx={{
              color: 'grey.300',
              borderColor: 'grey.700',
              '&:hover': {
                borderColor: 'grey.500',
                color: 'common.white',
                backgroundColor: 'rgba(255,255,255,0.06)',
              },
            }}
          >
            <T>Ver la plataforma</T>
          </Button>
        </Box>

        {/* LLM Mock */}
        <Box sx={{ mt: { xs: 7, md: 12.5 } }}>
          <Box
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '11px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'text.muted',
              pb: 2,
              borderBottom: '1px solid #1f2422',
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <span>
              <T>VISTA EN VIVO · 3 motores · misma consulta</T>
            </span>
            <Box
              component="span"
              sx={{
                color: 'text.muted',
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              <T>{elapsedLabel}</T>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: '1px',
              bgcolor: 'transparent',
            }}
          >
            {LLM_CARDS.map((card) => (
              <Box
                key={card.engine}
                component="article"
                sx={{
                  bgcolor: 'grey.900',
                  p: { xs: '20px', md: '28px' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: MONO_FONT,
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'text.muted',
                    }}
                  >
                    <T>{card.engine}</T>
                  </Typography>
                  <Box
                    sx={{
                      fontFamily: MONO_FONT,
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'primary.glow',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                      '@keyframes dotPing': {
                        '0%': { boxShadow: '0 0 0 0px rgba(26,191,159,0.55)' },
                        '70%': { boxShadow: '0 0 0 9px rgba(26,191,159,0)' },
                        '100%': { boxShadow: '0 0 0 0px rgba(26,191,159,0)' },
                      },
                      '&::before': {
                        content: '""',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#1abf9f',
                        flexShrink: 0,
                        animation: 'dotPing 2.4s ease-out infinite',
                      },
                    }}
                  >
                    <T>CITADO</T>
                  </Box>
                </Box>

                <Box
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '12px',
                    color: 'text.muted',
                    p: '10px 12px',
                    bgcolor: 'grey.800',
                    borderLeft: '2px solid',
                    borderColor: 'grey.500',
                    borderRadius: '0 3px 3px 0',
                    lineHeight: 1.5,
                  }}
                >
                  <T>{card.prompt}</T>
                </Box>

                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: 1.65,
                    color: 'grey.200',
                    flex: 1,
                  }}
                >
                  <T>{card.answer}</T>
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: '20px',
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'grey.800',
                    fontFamily: MONO_FONT,
                    fontSize: '11px',
                    color: 'text.muted',
                    letterSpacing: '0.06em',
                    '& strong': {
                      color: 'common.white',
                      fontWeight: 600,
                      ml: '5px',
                    },
                  }}
                >
                  <span>
                    <T>POS </T>
                    <strong>
                      <T>{card.pos}</T>
                    </strong>
                  </span>
                  <span>
                    <T>SHARE </T>
                    <strong>
                      <T>{card.share}</T>
                    </strong>
                  </span>
                  <span>
                    <T>SENT </T>
                    <strong>
                      <T>{card.sent}</T>
                    </strong>
                  </span>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* KPIs */}
        <DividedGrid
          dark
          cols={4}
          itemSx={{ p: { xs: '28px 20px', md: '32px 24px' } }}
          sx={{ mt: 4, mb: 6 }}
        >
          {KPIS.map((kpi, i) => (
            <Box key={kpi.label}>
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '10.5px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'text.muted',
                  mb: 2,
                }}
              >
                <T>{kpi.label}</T>
              </Typography>
              {/* ref target: GSAP writes directly to textContent */}
              <Typography
                ref={(el) => {
                  kpiRefs.current[i] = el;
                }}
                sx={{
                  fontSize: 'clamp(28px, 3vw, 40px)',
                  fontWeight: 300,
                  letterSpacing: '-0.03em',
                  color: kpi.accent ? 'primary.glow' : 'common.white',
                  lineHeight: 1,
                  mb: 2,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <T>{kpi.value}</T>
              </Typography>
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '11px',
                  color: 'text.muted',
                  letterSpacing: '0.02em',
                }}
              >
                <T>{kpi.sub}</T>
              </Typography>
            </Box>
          ))}
        </DividedGrid>
      </Box>
    </Box>
  );
}
